import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, ArrowLeft, Heart, Users, Shield, Sparkles, CheckCircle } from "lucide-react";
import HavnsLogo from "../components/HavnsLogo";
import Footer from "../components/Footer";

const Philosophy = () => {
  const navigate = useNavigate();

  const values = [
    {
      title: "Independence",
      description: "We celebrate the choice to be single. It's not a phase—it's a lifestyle we honor and protect.",
    },
    {
      title: "Authenticity",
      description: "Real connections require real people. Every member is verified, every interaction is genuine.",
    },
    {
      title: "Respect",
      description: "Boundaries aren't negotiable. We create space where 'no' is always respected.",
    },
  ];

  const principles = [
    {
      number: "01",
      title: "Quality Over Quantity",
      description: "We'd rather have 100 genuine members than 10,000 casual users. Every application is reviewed, every member matters.",
    },
    {
      number: "02",
      title: "Platonic by Design",
      description: "This isn't a dating app with extra steps. Every feature, every prompt, every interaction is designed for friendship.",
    },
    {
      number: "03",
      title: "Trust Through Verification",
      description: "Aadhaar KYC isn't bureaucracy—it's a promise. When you meet someone from Havns, you know they're real.",
    },
    {
      number: "04",
      title: "Zero Tolerance",
      description: "Romantic advances aren't just discouraged—they're grounds for immediate removal. We protect our community fiercely.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FBF9F7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FBF9F7]/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
          <HavnsLogo className="text-2xl" linkTo="/" />
          
          {/* Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/philosophy" 
              className="text-[#666] hover:text-[#1A1A1A] text-sm font-medium tracking-wide transition-colors"
            >
              Philosophy
            </Link>
            <Link 
              to="/movement" 
              className="text-[#666] hover:text-[#1A1A1A] text-sm font-medium tracking-wide transition-colors"
            >
              Movement
            </Link>
            <Link 
              to="/insights" 
              className="text-[#666] hover:text-[#1A1A1A] text-sm font-medium tracking-wide transition-colors"
            >
              Insights
            </Link>
          </div>

          <Button
            onClick={() => navigate("/")}
            className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-8 py-6 text-sm font-medium"
          >
            Join Havns
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#666] hover:text-[#1A1A1A] mb-12 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-6">
                Our Philosophy
              </p>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#1A1A1A] leading-[1.1] mb-8">
                At the heart<br />
                <span className="italic">of Havns.</span>
              </h1>
            </div>
            <div className="aspect-[4/3] rounded-[2rem] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&fit=crop"
                alt="Friends connecting"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-6">
              Havns Today
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] leading-tight mb-8">
              We want to create a world where being single is celebrated,
            </h2>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#666] leading-tight italic">
              by connecting independent souls for life's adventures.
            </h2>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#FBF9F7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-6">
                Our Values
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] leading-tight mb-6">
                What we believe in.
              </h2>
              <p className="text-xl text-[#666] leading-relaxed">
                These aren't just words on a page. They're the foundation of every 
                decision we make, every feature we build, every member we welcome.
              </p>
            </div>
            <div className="space-y-8">
              {values.map((value, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-8">
                  <h3 className="font-serif text-2xl text-[#1A1A1A] mb-3">
                    {value.title}
                  </h3>
                  <p className="text-[#666] leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Image Break */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="aspect-[21/9] rounded-[2rem] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1400&fit=crop"
              alt="Community gathering"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-24 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-16">
            <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-6">
              Our Principles
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl text-white leading-tight">
              We put our values in action.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {principles.map((principle, idx) => (
              <div key={idx} className="border-t border-white/20 pt-8">
                <span className="text-[#C5B358] font-mono text-sm">{principle.number}</span>
                <h3 className="font-serif text-2xl text-white mt-4 mb-3">
                  {principle.title}
                </h3>
                <p className="text-white/70 leading-relaxed">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Soloist Difference */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-6">
              Our Approach
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A]">
              The Soloist Difference
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Soloist Prompts",
                description: "No 'looking for' or relationship goals. Our prompts focus on travel dreams, professional expertise, and shared interests.",
              },
              {
                title: "Journey Matching",
                description: "Find companions for your Japan trip or Iceland adventure. Our algorithm matches travel styles, not love languages.",
              },
              {
                title: "Local Circles",
                description: "Coffee meetups, weekend treks, museum visits. Build your local network of like-minded independents.",
              },
              {
                title: "Manifesto Agreement",
                description: "Before any message is sent, members confirm their platonic intent. It's a small step that makes a big difference.",
              },
              {
                title: "Verified Profiles",
                description: "Aadhaar KYC ensures every face is real. No catfish, no bots, no surprises.",
              },
              {
                title: "Report & Protect",
                description: "One-tap reporting for romantic advances. We review every report and act swiftly to protect our community.",
              },
            ].map((feature, idx) => (
              <div key={idx} className="bg-[#FBF9F7] rounded-2xl p-8">
                <h3 className="font-serif text-xl text-[#1A1A1A] mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#666]">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#FBF9F7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mb-8">
            Ready to join us?
          </h2>
          <Button
            onClick={() => navigate("/")}
            className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-12 py-7 text-base font-medium inline-flex items-center gap-3"
          >
            Get Started
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Philosophy;
